/**
 * outreachSurfaces — the words and small arithmetic behind the outreach-list SURFACES: the lists
 * page, a list's page, the Today section, the sidebar item, and the lead-page chip
 * (docs/crm-outreach-lists-plan.md §B7).
 *
 * outreachLogic.ts owns the shared vocabulary (bucket, outcome, exclusion and flag labels) and the
 * wizard math; this file only composes those into the lines these pages print, plus the defensive
 * parse of the worklist poll's `outreach` block. Same rule as outreachLogic: React-free and
 * axios-free, so the tests need no mocks, and every recruiter-facing word is decided here rather
 * than ad hoc in a component — the owner's standing complaint is phrasing a person can't read.
 */
import { EXCLUSION_LABELS, SERVABLE_BUCKETS, TEXT_BLOCK_LABELS, shortWhen } from './outreachLogic';
import type {
  LeadOutreachInfo,
  OutreachCounts,
  OutreachExclusion,
  OutreachListDetail,
  OutreachListStatus,
  OutreachListSummary,
  OutreachMember,
  OutreachMessageMode,
  OutreachSettings,
  WorkQueueListCounts,
  WorklistOutreachCounts,
} from './types';

type Fmt = (iso: string) => string;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A route param is only worth a request when it could be a list id at all. */
export function isUuid(v: string | null | undefined): v is string {
  return typeof v === 'string' && UUID_RE.test(v);
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------------------------------------
// The worklist poll's `outreach` block (sidebar badge, Today section, assignment notice)
// ---------------------------------------------------------------------------------------------

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * The `outreach` block of GET /worklist?counts=true, or null.
 *
 * Null means "this api doesn't serve outreach lists" — an older deploy (plan §8 push order: the
 * backend ships first, but a frontend can meet an api that predates it) or a malformed body. The
 * sidebar item and the Today section both hide on null, so a missing block must never be read as
 * "zero lists": that would show admins an item that opens onto an api that 404s.
 */
export function parseWorklistOutreach(raw: unknown): WorklistOutreachCounts | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.servable !== 'number' || !Array.isArray(o.lists)) return null;
  const lists: WorklistOutreachCounts['lists'] = [];
  for (const row of o.lists) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const id = str(r.id);
    if (!id) continue;
    lists.push({
      id,
      name: str(r.name) ?? 'Outreach list',
      study_title: str(r.study_title) ?? '',
      assigned_open: num(r.assigned_open),
      replied: num(r.replied),
      untouched: num(r.untouched),
      retry: num(r.retry),
      launched_at: str(r.launched_at),
      created_by_name: str(r.created_by_name),
    });
  }
  return { servable: num(o.servable), urgent: num(o.urgent), waiting: num(o.waiting), lists };
}

/**
 * Whether the sidebar shows "Outreach lists". Admins always (it is where they manage lists) —
 * but only when the api serves the feature at all; everyone else only while they are on a list.
 */
export function showOutreachNav(isAdmin: boolean, outreach: WorklistOutreachCounts | null): boolean {
  if (!outreach) return false;
  return isAdmin || outreach.lists.length > 0;
}

/** People on one of my lists I can work right now (a reply, someone not called yet, a due retry). */
export function listServable(l: { replied: number; untouched: number; retry: number }): number {
  return (l.replied || 0) + (l.untouched || 0) + (l.retry || 0);
}

/** "3 replied · 42 not called yet · 5 to try again today" — zeros left out. */
export function todayListLine(l: WorklistOutreachCounts['lists'][number]): string {
  const parts: string[] = [];
  if (l.replied) parts.push(`${l.replied.toLocaleString()} replied`);
  if (l.untouched) parts.push(`${l.untouched.toLocaleString()} not called yet`);
  if (l.retry) parts.push(`${l.retry.toLocaleString()} to try again today`);
  if (parts.length) return parts.join(' · ');
  // The per-list row does not count due callbacks or agent hand-backs (those have their own
  // Today sections above), so this must not claim "nobody to call" outright.
  if (l.assigned_open) return `${plural(l.assigned_open, 'person', 'people')} still open — nobody new to call or retry right now`;
  return 'Everyone you were given is finished';
}

/**
 * The line shown instead of entering the work mode when a list has nothing servable
 * ("Everyone on your list has been tried — next tries start Tue 9:00 (12 waiting)").
 *
 * Right after a text or agent list launches, nobody is callable because everyone is waiting on
 * the first message (and the wait after it) — nobody has been tried, so the line must not say
 * they have: "Nobody on your list can be called yet — calls start Tue 1:00 PM (212 waiting on the
 * first message)". next_try_at is then when the first of them becomes callable, not the send time.
 */
export function nothingToWorkLine(c: WorkQueueListCounts | null | undefined, fmt: Fmt = shortWhen): string {
  // No counts after a start that returned nobody = the queue couldn't be read (the work-queue
  // context resolves 0 with null counts on a failed fetch) — say so, don't claim an empty list.
  if (!c) return "Couldn't check who's up next on your list. Please try again.";
  const firstMessage = num(c.waiting_first_message);
  if (firstMessage > 0) {
    const onFirst = `${firstMessage.toLocaleString()} waiting on the first message`;
    const others = Math.max(0, (c.waiting || 0) - firstMessage);
    if (!others) {
      return c.next_try_at
        ? `Nobody on your list can be called yet — calls start ${fmt(c.next_try_at)} (${onFirst}).`
        : `Nobody on your list can be called yet — ${plural(firstMessage, 'person is', 'people are')} waiting on the first message.`;
    }
    // Some were tried and are waiting for their next try; the rest haven't been tried at all.
    const detail = `${onFirst}, ${others.toLocaleString()} waiting for a next try, a reply or a booked call`;
    return c.next_try_at
      ? `Nobody on your list can be called right now — next calls start ${fmt(c.next_try_at)} (${detail}).`
      : `Nobody on your list can be called right now (${detail}).`;
  }
  if (c.next_try_at) {
    const when = fmt(c.next_try_at);
    const waiting = c.waiting > 0 ? ` (${c.waiting.toLocaleString()} waiting)` : '';
    return `Everyone on your list has been tried — next tries start ${when}${waiting}.`;
  }
  if (c.waiting > 0) {
    return `Everyone on your list has been tried — ${plural(c.waiting, 'person is', 'people are')} waiting on a text reply or a booked call.`;
  }
  if (c.remaining > 0) return 'Nobody on your list needs a call right now.';
  return 'Everyone on your list is finished.';
}

// ---------------------------------------------------------------------------------------------
// A list, as a row and as a page
// ---------------------------------------------------------------------------------------------

const ZERO_COUNTS: OutreachCounts = {
  members: 0, open: 0, done: 0, removed: 0, replied: 0, untouched: 0, retry: 0, waiting: 0,
  reached: 0, not_interested: 0, no_response: 0, advanced: 0,
};

/** Plan §B2.1 defaults — what a list without a stored value behaves as. */
const DEFAULT_SETTINGS: OutreachSettings = {
  retry_after_hours: 24, max_attempts: 3, call_after_text_hours: 3, agent_call_delay_days: 3,
  include_not_interested_texts: false, skip_recently_contacted_days: 7, text_old_leads_override: false,
  study_title_ack: false, start_at: '', pace_per_hour: 30,
};

/**
 * Fill every block a page reads with a safe default. The pages index straight into `counts`,
 * `launch`, `texts`… and one missing block in a response would otherwise take the whole CRM down
 * to its error boundary — a list page is never worth that.
 */
export function normalizeListSummary<T extends OutreachListSummary>(l: T): T {
  return {
    ...l,
    name: l.name || 'Outreach list',
    study: l.study || { id: '', title: '' },
    status: (l.status in LIST_STATUS_LABELS ? l.status : 'active') as OutreachListStatus,
    counts: { ...ZERO_COUNTS, ...(l.counts || {}) },
    mine: l.mine ? { ...ZERO_COUNTS, ...l.mine } : l.mine ?? null,
  };
}

export function normalizeListDetail(l: OutreachListDetail): OutreachListDetail {
  const base = normalizeListSummary(l);
  return {
    ...base,
    message_mode: (l.message_mode in MESSAGE_MODE_LABELS ? l.message_mode : 'none') as OutreachMessageMode,
    settings: { ...DEFAULT_SETTINGS, ...(l.settings || {}) },
    source: l.source || {},
    launch: {
      total: 0, prepared: 0, effective_start: null, excluded: {}, reopened: 0, owner_changes: 0,
      texts_planned: 0, stopped_automations: 0,
      ...((l.launch || {}) as Partial<OutreachListDetail['launch']>),
    },
    texts: { scheduled: 0, sent: 0, cancelled: 0, cancelled_reasons: {}, ...((l.texts || {}) as Partial<OutreachListDetail['texts']>) },
    recruiters: Array.isArray(l.recruiters) ? l.recruiters : [],
    agent_texts_pending: l.agent_texts_pending === true,
    viewer: { is_admin: false, is_recruiter: false, ...((l.viewer || {}) as Partial<OutreachListDetail['viewer']>) },
  };
}

export const LIST_STATUS_LABELS: Record<OutreachListStatus, string> = {
  launching: 'Setting up',
  active: 'Active',
  completed: 'Closed',
  failed: "Couldn't be set up",
};

export const LIST_STATUS_TONE: Record<OutreachListStatus, 'primary' | 'green' | 'gray' | 'red'> = {
  launching: 'primary',
  active: 'green',
  completed: 'gray',
  failed: 'red',
};

/**
 * Progress = finished of everyone actually on the list. Removed members (taken off by a manager,
 * or changed while the list was being set up) were never really worked, so they leave both sides.
 */
export function listProgress(c: Pick<OutreachCounts, 'members' | 'done' | 'removed'> | null | undefined): {
  finished: number;
  total: number;
  pct: number;
} {
  const total = Math.max(0, (c?.members || 0) - (c?.removed || 0));
  const finished = Math.min(total, Math.max(0, c?.done || 0));
  return { finished, total, pct: total > 0 ? Math.round((finished / total) * 100) : 0 };
}

/** "Setting up — 40 of 212 ready". */
export function launchProgressLine(launch: { prepared: number; total: number } | null | undefined): string {
  const total = Math.max(0, launch?.total || 0);
  const prepared = Math.min(total, Math.max(0, launch?.prepared || 0));
  if (!total) return 'Setting up…';
  return `Setting up — ${prepared.toLocaleString()} of ${total.toLocaleString()} ready`;
}

/** "Left off at launch: 12 scheduled, screened or enrolled in a study · 4 opted out of texts". */
export function excludedLine(excluded: Partial<Record<OutreachExclusion, number>> | null | undefined): string | null {
  const parts = Object.entries(excluded || {})
    .filter(([code, n]) => code !== 'merged' && typeof n === 'number' && n > 0)
    .sort((a, b) => (b[1] as number) - (a[1] as number))
    .map(([code, n]) => `${(n as number).toLocaleString()} ${lowerFirst(EXCLUSION_LABELS[code as OutreachExclusion] ?? 'left off for another reason')}`);
  return parts.length ? parts.join(' · ') : null;
}

function lowerFirst(s: string): string {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

export const MESSAGE_MODE_LABELS: Record<OutreachMessageMode, string> = {
  none: 'No first message — calls only',
  text: 'One text to start',
  agent: 'The texting agent starts the conversation',
};

/**
 * Why a list text was stopped, in words. The contract carries these as the scheduled message's
 * `last_error` code, which the server does not enumerate for us — so this reads the code for its
 * meaning rather than matching exact strings, and anything it cannot read becomes a neutral
 * sentence. A raw code is never shown.
 */
export function textCancelReasonLabel(code: string | null | undefined): string {
  const c = (code || '').toLowerCase();
  if (!c) return 'Stopped';
  // The api reduces `outreach:<reason>` / `blocked:<reason>` to the reason (crmOutreachLists
  // cancelReasonCode); the ones it writes today are matched exactly, then the rest by meaning.
  // Most specific first: "list_closed" must not read as "the lead was closed", an outcome code
  // must not read as a manager's cancel, and a bare "cancelled" claims no one did it.
  if (c === 'cancelled' || c === 'canceled') return 'Cancelled';
  // The send-time check could not finish, so the text was held back rather
  // than risk it (crmOutreachLists checkOutreachTextAtFire fails closed). It
  // was never sent — not a failed send — and the recruiter calls instead.
  if (c === 'check_failed') return "Couldn't confirm it was still OK to send";
  // A soft stop is "stop texting me", not a STOP opt-out or a do-not-contact:
  // they can still be called, so it must not read like the broader stop below.
  if (c === 'soft_stopped' || c === 'soft_stop') return 'They told us to stop texting';
  if (c === 'failed' || c.includes('timed_out')) return "Didn't go through";
  if (c.includes('too_many_attempts')) return "Didn't go through after several tries";
  if (c.includes('merge')) return 'Duplicate record was merged';
  if (c.includes('list_closed') || (c.includes('list') && c.includes('clos'))) return 'The list was closed';
  if (c.includes('launch_failed')) return "The list couldn't be set up";
  if (c.includes('changed_during_launch')) return 'Changed while the list was being set up';
  if (c.includes('not_prepared')) return 'They were never fully added to the list';
  if (c.includes('outcome') || c.includes('logged') || c === 'interested' || c === 'not_interested'
    || c === 'no_answer' || c === 'voicemail' || c === 'texted' || c === 'call_back' || c === 'try_later') {
    return 'A recruiter got to them first';
  }
  if (c.includes('wrong_number')) return 'Wrong number';
  if (c.includes('opt') || c.includes('stop')) return 'They opted out or asked us to stop';
  if (c.includes('consent')) return 'No permission to text them on file';
  if (c.includes('moved') || c.includes('study')) return 'Moved to another study';
  if (c.includes('schedul') || c.includes('enrol') || c.includes('random') || c.includes('screened')) return 'Already scheduled or enrolled';
  if (c.includes('advanced')) return 'They moved ahead in the pipeline';
  if (c.includes('no_response')) return 'No response after every try';
  if (c.includes('remov')) return 'Taken off the list';
  if (c.includes('not_open') || c.includes('finished') || c.includes('done')) return 'Already finished on the list';
  if (c.includes('not_active') || c.includes('inactive')) return 'The list was no longer running';
  if (c.includes('cancel') || c.includes('admin') || c.includes('manager')) return 'Texts were stopped by an admin';
  if (c.includes('closed')) return 'The lead was closed';
  if (c.includes('missing') || c.includes('gone')) return 'The record no longer exists';
  if (c.includes('quiet') || c.includes('hours')) return 'Outside texting hours';
  return 'Stopped for another reason';
}

/** "184 texts · 120 sent · 60 waiting to send · 4 stopped" — or null when nothing was texted. */
export function textsLine(t: { scheduled: number; sent: number; cancelled: number } | null | undefined): string | null {
  const scheduled = Math.max(0, t?.scheduled || 0);
  const sent = Math.max(0, t?.sent || 0);
  const cancelled = Math.max(0, t?.cancelled || 0);
  if (!scheduled && !sent && !cancelled) return null;
  const parts = [`${sent.toLocaleString()} sent`];
  if (scheduled) parts.push(`${scheduled.toLocaleString()} waiting to send`);
  if (cancelled) parts.push(`${cancelled.toLocaleString()} stopped`);
  return parts.join(' · ');
}

/** The stopped-text breakdown, merged by the words (two codes can read the same). */
export function cancelledReasonsLines(reasons: Record<string, number> | null | undefined): Array<{ label: string; count: number }> {
  const byLabel = new Map<string, number>();
  Object.entries(reasons || {}).forEach(([code, n]) => {
    if (typeof n !== 'number' || n <= 0) return;
    const label = textCancelReasonLabel(code);
    byLabel.set(label, (byLabel.get(label) || 0) + n);
  });
  return Array.from(byLabel.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

/** A member's first-text status, in words ("Sent Tue 10:02", "Not texted — opted out …"). */
export function memberTextLabel(m: Pick<OutreachMember, 'text' | 'text_block'>, fmt: Fmt = shortWhen): string {
  const t = m.text;
  if (!t || t.status === 'none') {
    if (m.text_block && m.text_block !== 'not_text_mode') return `Not texted — ${lowerFirst(TEXT_BLOCK_LABELS[m.text_block])}`;
    return '—';
  }
  switch (t.status) {
    case 'scheduled': return t.send_at ? `Scheduled ${fmt(t.send_at)}` : 'Scheduled';
    case 'sending': return 'Sending';
    case 'sent': return t.sent_at ? `Sent ${fmt(t.sent_at)}` : 'Sent';
    case 'failed': return "Didn't go through";
    case 'canceled': return `Stopped — ${lowerFirst(textCancelReasonLabel(t.cancel_reason))}`;
    default: return '—';
  }
}

// ---------------------------------------------------------------------------------------------
// A list's page: the people table and the manager's share-out
// ---------------------------------------------------------------------------------------------

export const MEMBERS_PAGE_SIZE = 50;

/** One people-table row with its arrays and nested blocks guaranteed (see normalizeListSummary). */
export function normalizeMember(m: OutreachMember): OutreachMember {
  return {
    ...m,
    // Not prepared yet (the launch is still adding them) comes as null — the table says so in words.
    bucket: m.bucket ?? null,
    contact: { id: m.contact?.id || '', name: m.contact?.name || '', phone: m.contact?.phone ?? null, lifecycle_status: m.contact?.lifecycle_status ?? null },
    flags: Array.isArray(m.flags) ? m.flags : [],
    attempts: typeof m.attempts === 'number' ? m.attempts : 0,
    max_attempts: typeof m.max_attempts === 'number' && m.max_attempts > 0 ? m.max_attempts : 3,
  };
}

/** "Showing 51–100 of 212" (or "Nobody matches these filters"). */
export function pageRangeLabel(offset: number, shown: number, total: number): string {
  if (!total || !shown) return total ? `Showing none of ${total.toLocaleString()}` : 'Nobody matches these filters';
  const from = offset + 1;
  const to = Math.min(total, offset + shown);
  return `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`;
}

export const MEMBER_STATE_FILTERS: Array<{ value: '' | 'open' | 'done' | 'removed'; label: string }> = [
  { value: 'open', label: 'Still open' },
  { value: 'done', label: 'Finished' },
  { value: 'removed', label: 'Taken off the list' },
  { value: '', label: 'Everyone' },
];

/**
 * Who a share-out goes to by default: the list's recruiters who are still on it and at their desk
 * today, minus the person whose people are being shared. Sharing onto someone who is out would
 * move the backlog from one empty chair to another.
 */
export function defaultShareRecipients(
  recruiters: Array<{ user_id: string; active: boolean; out_today?: boolean }>,
  excludeUserId?: string | null,
): string[] {
  return recruiters
    .filter((r) => r.active && !r.out_today && r.user_id !== excludeUserId)
    .map((r) => r.user_id);
}

/**
 * The people a manager can hand on from one recruiter: open members nobody has called yet. Read
 * from the members rows themselves (not a server filter the contract doesn't promise), so a
 * person who already got a call, a reply or a booked callback always stays with the recruiter
 * who has the history.
 */
export function untouchedMemberIds(members: Array<Pick<OutreachMember, 'id' | 'state' | 'bucket'>>): string[] {
  return members.filter((m) => m.state === 'open' && m.bucket === 'untouched').map((m) => m.id);
}

/** "Moved 12 people to Sarah" / "Shared 30 people between Sarah and Mike". */
export function sharedNotice(moved: number, names: string[]): string {
  const who = names.length <= 1
    ? names[0] || 'the chosen recruiters'
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  if (!moved) return 'Nobody needed moving — there was no one left who hadn\'t been called.';
  return names.length <= 1
    ? `Moved ${plural(moved, 'person', 'people')} to ${who}.`
    : `Shared ${plural(moved, 'person', 'people')} between ${who}.`;
}

// ---------------------------------------------------------------------------------------------
// The lead page (chip + the Callbacks card's cadence line)
// ---------------------------------------------------------------------------------------------

type LeadMember = NonNullable<LeadOutreachInfo['member']>;

/** "try 2 of 3" — numbers the try that is DUE, like the cadence's "Attempt N of M". */
export function tryOf(m: { attempts: number; max_attempts: number }): string {
  const max = m.max_attempts > 0 ? m.max_attempts : 3;
  return `try ${Math.min(Math.max(0, m.attempts) + 1, max)} of ${max}`;
}

/** The chip's words after "On outreach list: <name>": "try 2 of 3 · Sarah". */
export function leadChipDetail(m: LeadMember): string {
  const parts = [m.bucket === 'untouched' ? 'not called yet' : tryOf(m)];
  if (m.assignee?.name) parts.push(m.assignee.name);
  return parts.join(' · ');
}

/**
 * The Callbacks card's line while the lead is on a list — the list's own retry schedule replaces
 * the cadence counter (the cadence engine skips list leads, plan §B8):
 * "On Neuropathy re-engagement · try 2 of 3 · next try Tue 10:00".
 */
export function listCadenceLine(m: LeadMember, now: number = Date.now(), fmt: Fmt = shortWhen): string {
  const parts = [`On ${m.list_name}`, m.bucket === 'untouched' ? 'not called yet' : tryOf(m)];
  const next = m.next_attempt_at || m.waiting_until;
  if (next && Date.parse(next) > now) parts.push(`next try ${fmt(next)}`);
  else if (SERVABLE_BUCKETS.has(m.bucket)) parts.push('ready to call now');
  return parts.join(' · ');
}
